import debounce from 'debounce';
import { authorizeTransferRequest, createTransferRequest } from 'graphql/wire-transfers/mutations';
import {
  getBlockchainData,
  getMembesrTitle,
  getWireTrannsfersResponse,
  GQLGetOrderMember,
  GQLGetWireTransfers,
} from 'graphql/wire-transfers/queries';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueries } from 'react-query';
import { useParams } from 'react-router-dom';
import useAlert from 'utils/alert';
import { getOrder } from '../new-disbursments/services/querys';

import { InstructionsRequest, SearchParam, SortBy, TransferBlockchainData } from './types';
import { filterTransferList, getComparationFn } from './utils';
import { AccountingType } from 'v2-types/order';
import { getBlockchain } from './services/querys';

export type wireDataListType = {
	data_id: string;
	wire: {
		name: string;
		bank: string;
		account: string;
		routing: string;
	}[];
};

const useWireTransfers = ({ idTitleCompany }) => {
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();
  const [openBackdrop, setOpenBackdrop] = useState<boolean>(false);
  const searchParamsRef = useRef<SearchParam>({ status: '' });
  const sortyByRef = useRef<SortBy>({ property: '' });
  const amountFilterRef = useRef<{ min?: number; max?: number }>({});
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(5);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalRows, setTotalRows] = useState<number>(0);

  const referencesRef = useRef<TransferBlockchainData[]>([]);
  const [transferPages, setTransferPages] = useState<Array<TransferBlockchainData[]>>([]);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(true);
  const [disbursments, setDisbursments] = useState<AccountingType[]>([]);
  const [selecteDisbursments, setSelecteDisbursments] = useState('');
  const [agencyIds, setAgencyIds] = useState<string[]>([]);
  const [dataIds, setDataIds] = useState<string[]>([]);
  const [wireDataList, setWireDataList] = useState<wireDataListType[]>([]);

  const getAllInvolvedPartyIds = (disbursements: AccountingType[]): string[] => {
    const ids = disbursements.flatMap(
      (disb) =>
        disb.accounted_for?.flatMap(
          (accounted) =>
            accounted.record.involved_parties?.map((party) => party._id).filter(Boolean) ?? []
        ) ?? []
    );

    return [...new Set(ids)];
  };

  useQuery(['accounting-order-disbursments', orderId], () => getOrder(orderId), {
    enabled: !!orderId,
    refetchOnWindowFocus: false,
    onSuccess: (data) => {
      if (data) {
        const filteredDisbursments = data.getOrder.accounting.filter(
          (disbursment) =>
            disbursment.kind === 'disbursement' &&
						disbursment.posted &&
						disbursment.code === 'wire' &&
						(disbursment.accounted_for?.[0]?.amount ?? 0) > 0
        );

        const titleCompany = data.getOrder.parties.find((party) =>
          party.kinds.includes('Title Company')
        );

        const relatedAgencyIds = [
          ...(titleCompany?.member_of || [])
            .filter((member) => member.kind.includes('Title Agency'))
            .map((member) => member._id),
          titleCompany?._id,
        ].filter((id): id is string => Boolean(id));

        setAgencyIds(relatedAgencyIds);

        setDisbursments(filteredDisbursments);
        const ids = getAllInvolvedPartyIds(filteredDisbursments);
        setDataIds([...ids]);
      }
    },
  });

  const results = useQueries(
    agencyIds.map((id) => ({
      queryKey: ['partyMember', id],
      queryFn: () => getMembesrTitle({ id }),
      enabled: !!id,
    }))
  );

  const allMembers = results.map((r) => r.data?.getParty).filter(Boolean);

  const members = allMembers
    .flatMap((group) => {
      return (group?.members ?? [])
        .filter((mem) => Boolean(mem?.login_email))
        .map((mem) => ({
          ...mem,
          name: `${mem.name} from ${group?.name}`,
        }));
    })
    .sort((a, b) => {
      return (a?.name ?? '').localeCompare(b?.name ?? '');
    });

  useEffect(() => {
    if (!dataIds.length) return;
    const fetchAll = async () => {
      await Promise.all(dataIds.map((id) => getBlockchain(id)));
    };

    fetchAll();
  }, [dataIds]);

  const [open, setOpen] = useState<{
		status: boolean;
		location: string;
		data: TransferBlockchainData | undefined;
	}>({
	  status: false,
	  location: '',
	  data: undefined,
	});

  const paginateTransfers = useCallback(({
    transfers,
    transfersPerPage,
  }: {
		transfers: TransferBlockchainData[];
		transfersPerPage: number;
	}) => {
    const listLength = transfers.length;
    const totalPagesResult = Math.ceil(listLength / transfersPerPage);
    const paginationList: Array<TransferBlockchainData[]> = [];

    Array.from(Array(totalPagesResult).keys()).forEach((index) => {
      const page = transfers.slice(index * transfersPerPage).slice(0, transfersPerPage);
      paginationList.push(page);
    });

    setTotalPages(totalPagesResult);
    setTotalRows(listLength);
    setRowsPerPage(transfersPerPage);
    setTransferPages(paginationList);

    if (currentPage > totalPagesResult) {
      setCurrentPage(1);
    }
    referencesRef.current = transfers;
  }, [currentPage]);

  const changeRowsPerPage = ({ rows }: { rows: number }) => {
    paginateTransfers({ transfers: referencesRef.current, transfersPerPage: rows });
  };

  const wireTransfersResponse = useQuery<GQLGetWireTransfers>(
    ['wireTransfers', orderId],
    () => getWireTrannsfersResponse({ orderId }),
    {
      onSuccess: (data) => {
        if (data.getTransfers) {
          const referencesData = data.getTransfers.map((row) => ({
            reference: row.reference,
            date: null,
            document: null,
            isDataLoading: true,
          }));

          paginateTransfers({
            transfers: referencesData as TransferBlockchainData[],
            transfersPerPage: rowsPerPage,
          });

          getBlockchainData({
            dataId: orderId,
            references: referencesData.map((row) => row.reference),
          });

          setIsDataLoading(false);
        }
      },
      refetchOnWindowFocus: false,
    }
  );

  const membersResponse = useQuery<GQLGetOrderMember>(
    ['members', orderId],
    () => getMembesrTitle({ id: idTitleCompany }),
    { refetchOnWindowFocus: false }
  );

  const { mutate: createTransfer } = useMutation(createTransferRequest, {
    onSuccess: () => {
      setTimeout(() => {
        wireTransfersResponse.refetch();
        showAlert('Transfer was created successfully', 'success');
      }, 1000);
    },
  });

  const { mutate: authorizeTransfer } = useMutation(authorizeTransferRequest, {
    onSuccess: () => {
      setTimeout(() => {
        wireTransfersResponse.refetch();
        showAlert('Authorization responded successfully', 'success');
      }, 1000);
    },
  });

  const handleCreateWire = (authorization: string[], instructions: InstructionsRequest[]) => {
    createTransfer({
      orderId,
      instructions,
      authorization,
    });
  };

  const handleAuthTransfer = (action) => {
    authorizeTransfer({ orderId, reference: open.data?.reference ?? '', action });
  };

  const handleToggleModals = (
    location: string = '',
    data: TransferBlockchainData | undefined = undefined
  ) => {
    setOpen({ status: !open.status, location, data });
  };

  const searchTransfers = useCallback(() => {
    setOpenBackdrop(true);
    const { status } = searchParamsRef.current;
    const { property, operator = '' } = sortyByRef.current;
    const { min: amountMin, max: amountMax } = amountFilterRef.current;

    const transferListFiltered = filterTransferList({
      transferList: referencesRef.current,
      status,
      amountMin,
      amountMax,
    });

    const comparationFn = getComparationFn({ property, operator });
    if (comparationFn) {
      transferListFiltered.sort(comparationFn);
    }

    paginateTransfers({ transfers: transferListFiltered, transfersPerPage: rowsPerPage });
    setOpenBackdrop(false);
  }, [rowsPerPage, paginateTransfers]);

  const debounceSearch = debounce(searchTransfers, 1000);

  const onFilterChange = ({ value, paramKey }: { value: string; paramKey: keyof SearchParam }) => {
    debounceSearch.clear();
    searchParamsRef.current[paramKey] = value;
    debounceSearch();
  };

  const onSort = ({ property, operator = 'descending' }: SortBy) => {
    sortyByRef.current = {
      property,
      operator,
    };
    searchTransfers();
  };

  const onUpdateAmountLimits = ({ min, max }: { min?: number; max?: number }) => {
    amountFilterRef.current = {
      min,
      max,
    };

    searchTransfers();
  };

  const clearFilters = () => {
    searchParamsRef.current.status = '';
    sortyByRef.current = { property: '' };
    amountFilterRef.current = {};

    searchTransfers();
  };

  const handleChangeDisbursements = (disbursement) => {
    setSelecteDisbursments(disbursement);
  };

  return {
    isDataLoading,
    transferPages,
    currentPage,
    totalPages,
    rowsPerPage,
    totalRows,
    setCurrentPage,
    changeRowsPerPage,
    open,
    handleToggleModals,
    members,
    handleCreateWire,
    handleAuthTransfer,
    openBackdrop,
    onFilterChange,
    onSort,
    onUpdateAmountLimits,
    clearFilters,
    disbursments,
    selecteDisbursments,
    handleChangeDisbursements,
    wireDataList,
    setWireDataList,
    paginateTransfers,
    referencesRef,
  };
};

export default useWireTransfers;
